import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useState } from 'react';
import {
  RadioGroup, Radio, FormControlLabel, TextField, Avatar, Tooltip, Checkbox,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { NETWORK_IDS, USER_TYPES } from 'lib/constants';
import { useApi } from 'lib/contexts/ApplicationState';
import { useSnackbar } from 'notistack';
import { toNumber, isEmpty } from 'lodash';
import { UserToSave, NetworkUser, User } from 'lib/types/users';
import { imageAvatarUrl } from 'lib/utils/imageUtils';
import { NetworkIcon } from 'elements/NetworkIcon';
import { Feedback } from 'components/Feedback';
import { useModal } from 'mui-modal-provider';

interface Props {
  onSave: (account: User) => Promise<void>;
  onCancel: () => void;
  username?: string;
}

const NETWORK_ORDER = [
  NETWORK_IDS.instagram,
  NETWORK_IDS.tiktok,
  NETWORK_IDS.twitch,
  NETWORK_IDS.threads,
  NETWORK_IDS.twitter,
  NETWORK_IDS.youtube,
  NETWORK_IDS.facebook,
  NETWORK_IDS.snapchat,
] as const;

interface SocialOptions {
  twitter: NetworkUser;
  instagram: NetworkUser;
  tiktok: NetworkUser;
  existingUser: NetworkUser;
  twitch: NetworkUser;
  youtube: NetworkUser;
  facebook: NetworkUser;
  threads: NetworkUser;
  snapchat: NetworkUser;
}

const initialUser = {
  userTypeId: USER_TYPES.creator.id,
};

export const NewUserToCreditModal = ({
  onSave, onCancel, username,
}: Props) => {
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();

  const [searchTerm, setSearchTerm] = useState(username);
  const [options, setOptions] = useState<SocialOptions>();
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [newUser, setNewUser] = useState<Partial<UserToSave>>(initialUser);
  const [postingAccounts, setPostingAccounts] = useState<NetworkUser[]>([]);

  const notFound = (result: SocialOptions) => {
    if (!result) return true;

    return isEmpty(result.existingUser) && isEmpty(result.instagram)
      && isEmpty(result.tiktok) && isEmpty(result.twitter) && isEmpty(result.youtube)
      && isEmpty(result.facebook) && isEmpty(result.threads);
  };

  const isAccountSelected = (option: NetworkUser) => (
    postingAccounts.some((account) => (
      account.username === option.username && account.network === option.network
    ))
  );

  const isDefaultAccount = (option: NetworkUser) => (
    newUser
    && newUser.username === option.username
    && newUser.network === option.network
  );

  const handleChangeDefaultAccount = (option: NetworkUser) => {
    if (isDefaultAccount(option)) {
      updateDefaultAccount(undefined);
      return;
    }
    updateDefaultAccount(option);
  };

  const updateDefaultAccount = (account?: NetworkUser, clearUsername = false) => {
    setNewUser({ ...newUser, ...account });
    if (clearUsername) {
      setNewUser((prev) => ({ ...prev, username: undefined }));
    }
  };

  const search = async () => {
    setOptions(undefined);
    const regEx = new RegExp(/^[\w.]+$/i);
    if (!searchTerm || !regEx.test(searchTerm)) {
      enqueueSnackbar('Please provide a valid username', {
        variant: 'error',
      });
      return;
    }
    try {
      setIsSearching(true);
      const results = await API.lookUpAllNetworks(searchTerm, true);
      setOptions(results);
    } catch (err) {
      enqueueSnackbar(`Something went wrong: ${err}`, {
        variant: 'error',
      });
    }
    setIsSearching(false);
  };

  const getOptions = () => {
    if (!options) {
      return null;
    }
    if (notFound(options)) {
      return (
        <div>
          <b>
            @
            {searchTerm}
          </b>
          {' '}
          wasn&apos;t found on any platforms.
          <br />
          You can still manually add them without socials.
        </div>
      );
    }
    if (!isEmpty(options.existingUser)) {
      return (
        <div>
          A user with that username already exists:
          {socialOption(options.existingUser, 0)}
        </div>
      );
    }

    return (
      <>
        {NETWORK_ORDER.map((networkId) => {
          const networkKey = Object.keys(NETWORK_IDS).find(
            (key) => NETWORK_IDS[key as keyof typeof NETWORK_IDS] === networkId,
          ) as keyof SocialOptions;
          return socialOption(options[networkKey], networkId);
        })}
      </>
    );
  };

  const handleSelect = (option: NetworkUser) => {
    setPostingAccounts((prev) => {
      const newAccounts = [...prev, option];
      // Set as default account if it's the first one selected
      if (prev.length === 0) {
        updateDefaultAccount(option);
      }
      return newAccounts;
    });
  };

  const handleUnselect = (option: NetworkUser) => {
    setPostingAccounts((prev) => {
      const newAccounts = prev.filter((account) => (
        account.username !== option.username || account.network !== option.network
      ));

      // If we're removing the default account, set the next one as default
      if (newUser.username === option.username
        && newUser.network === option.network) {
        const nextDefault = newAccounts[0];
        if (nextDefault) {
          updateDefaultAccount(nextDefault);
        } else {
          updateDefaultAccount(undefined, true);
        }
      }

      return newAccounts;
    });
  };

  const socialOption = (option: NetworkUser, networkId: number) => {
    if (isEmpty(option)) {
      return null;
    }

    const isSelected = isAccountSelected(option);
    const isDefault = isDefaultAccount(option);

    return (
      <div
        key={`${option.username}-${networkId}`}
        className="flex items-center gap-4 py-2 pr-44 pl-14"
      >
        <Checkbox
          checked={isSelected}
          onChange={() => {
            if (isSelected) {
              handleUnselect(option);
            } else {
              handleSelect(option);
            }
          }}
          disabled={networkId === 0 || !!option.id}
        />
        {networkId === 0 ? (
          <img alt="logo" src="/red-logo.png" width={24} height={24} />
        ) : (
          <NetworkIcon network={networkId} iconColor="color" />
        )}
        <Avatar
          sx={{ width: 48, height: 48 }}
          alt={option.username}
          src={option.avatarUrl && imageAvatarUrl(option.avatarUrl)}
        />
        <div className="text-sm">
          <div>{option.name}</div>
          <div className="font-semibold">
            @
            {option.username}
          </div>
          <div className="flex space-x-2 text-xs text-gray-500">
            <span>
              <PeopleIcon fontSize="small" style={{ marginRight: '5px' }} />
              {option.followersCount?.toLocaleString() || '--'}
            </span>
            {option.location && (
              <span>
                |
                <LocationOnIcon fontSize="small" style={{ margin: '0 3px' }} />
                {option.location}
              </span>
            )}
          </div>
        </div>
        <div className="ml-auto">
          <Tooltip
            title={isDefault ? 'This is the default account for this user.' : 'Set as default account'}
            placement="top"
            arrow
          >
            <button
              type="button"
              onClick={() => handleChangeDefaultAccount(option)}
              disabled={networkId === 0}
            >
              {isDefault ? <StarIcon color="primary" /> : <StarBorderIcon />}
            </button>
          </Tooltip>
        </div>
      </div>
    );
  };

  const save = async () => {
    if (!options) return;
    setIsSaving(true);
    const createdUser = await API.createUser(newUser as User);
    await API.addPostingAccounts(
      postingAccounts.map((acc) => ({
        ...acc,
        userId: createdUser.id,
        network: acc.network,
      })),
      createdUser.id,
    );

    enqueueSnackbar('User created successfully!', { variant: 'success' });
    onSave(createdUser);
  };

  const handleModal = () => {
    const modal = showModal(Feedback, {
      onClose: () => {
        modal.destroy();
      },
      title: 'Report an issue',
      text: 'Add a brief description of the bug or platform issue you are experiencing, and how we can reproduce it.',
    });
  };

  return (
    <Dialog open maxWidth="md">
      <div className="p-6">
        <div className="absolute right-2 top-2">
          <IconButton onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        </div>
        <h1 className="text-xl font-semibold mb-2">Add user by social media handle</h1>
        <p className="mb-4">
          We could not find the user in our system.
          Let&apos;s add them, so you can give them some credits!
        </p>
        <div className="flex w-full gap-4 mb-4">
          <TextField
            fullWidth
            autoFocus
            variant="outlined"
            InputProps={{
              startAdornment: '@',
            }}
            label="social media handle"
            placeholder="Search TikTok, Instagram, YouTube, Facebook, Twitch and Twitter handles"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
          <button
            disabled={!searchTerm || isSearching}
            type="button"
            className="btn-primary"
            onClick={search}
          >
            {isSearching ? 'Searching...' : 'Search'}
          </button>
        </div>
        <div className="font-semibold mb-2">{options && !notFound(options) && `Select all of ${searchTerm}'s accounts`}</div>
        {isSearching ? <div className="w-full text-center">Searching...</div>
          : (
            <div>
              {getOptions()}
            </div>
          )}
        {postingAccounts.length > 0 && (
          <div>
            <RadioGroup
              aria-labelledby="type-radio-group"
              name="type-radio-group"
              value={newUser.userTypeId}
              onChange={(e) => setNewUser({ ...newUser, userTypeId: toNumber(e.target.value) })}
            >
              <div className="font-semibold mt-2">This account is for a...</div>
              <div className="flex">
                <FormControlLabel
                  key={USER_TYPES.creator.key}
                  value={USER_TYPES.creator.id}
                  control={<Radio />}
                  label={<b>{USER_TYPES.creator.key}</b>}
                />
                <FormControlLabel
                  key={USER_TYPES.company.key}
                  value={USER_TYPES.company.id}
                  control={<Radio />}
                  label={<b>{USER_TYPES.company.key}</b>}
                />
              </div>
            </RadioGroup>
          </div>
        )}
        <div className="flex flex-wrap justify-between items-baseline w-full">
          <div className="w-full sm:w-1/3 mb-2">
            <button type="button" className="btn-primary-outlined-xs" onClick={handleModal}>Need help?</button>
          </div>
          <div className="w-full sm:w-2/3 text-right">
            <button
              className="btn-primary-outlined mr-2"
              onClick={onCancel}
              type="button"
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={isSearching || isSaving}
              onClick={save}
              type="button"
            >
              <span className="flex items-center gap-2">
                Save
                {(isSaving || isSearching) && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-auto" />
                )}
              </span>
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
